/**
 * Step 6 (Platform-Eng workflow) — SkillInvocationForm.
 *
 * <p>Structured invocation surface that closes the Tier-2 gap the
 * platform-engineer walkthrough flagged: every skill invocation
 * today goes through {@code TestRunSidePanelV2}, the conversational
 * planner. For deterministic ops work (2am incident replay, runbook
 * automation) that's the wrong tool — the operator wants
 * "pick skill → pick export → fill args → submit," not a natural-
 * language prompt the planner has to interpret.</p>
 *
 * <p>The form bypasses the planner entirely and posts directly to
 * {@code POST /api/v1/agents/{id}/skills/{sid}/invoke}, which runs
 * the same {@code PolicyEnforcementGate} + audit chain pipeline
 * the conversational path uses. From the policy decisions table's
 * perspective the two paths are indistinguishable — exactly what
 * we want for "the structured form is a UI veneer, not a backdoor."</p>
 *
 * <h3>Skill-type behavior</h3>
 *
 * <p>Step 6 v1 backend only dispatches {@code wasm_skill}. For
 * non-wasm types this slideout still opens (so the operator gets a
 * consistent affordance from the BuildTab "Run…" button), but the
 * body shows a clear banner explaining structured invocation isn't
 * yet plumbed for that type — with guidance to use the
 * conversational TestRun panel instead.</p>
 *
 * <h3>Editor</h3>
 *
 * <p>Args are authored as JSON in a Monaco editor (lazy-loaded, same
 * pattern as {@code RegoEditorSlideout} and {@code WorkflowCodeTab}).
 * Falls back to a styled {@code <textarea>} if Monaco's bundle fails
 * to resolve. Pre-submit JSON.parse validation surfaces a precise
 * error in the response pane so the operator can fix the payload
 * without losing it.</p>
 *
 * <h3>Response pane</h3>
 *
 * <p>Three states:</p>
 * <ul>
 *   <li><b>allowed</b> → green pill, elapsedMs, JSON output viewer
 *       with Copy button.</li>
 *   <li><b>blocked</b> → red pill with the policy reason, count of
 *       policy decisions recorded. The operator can cross-reference
 *       these in the Policy Decisions tab (Step 7).</li>
 *   <li><b>error</b> → red banner with the kind (UNKNOWN_EXPORT /
 *       UNSUPPORTED_SKILL_TYPE / WASM_EXECUTION_FAILED / …) and
 *       message verbatim from the backend.</li>
 * </ul>
 *
 * <h3>Props</h3>
 *
 * <ul>
 *   <li>{@code isOpen}, {@code onClose} — slideout visibility.</li>
 *   <li>{@code agentId} — UUID of the owning agent. Required.</li>
 *   <li>{@code skill} — the skill row (id, name, skillType,
 *       riskLevel, wasmEntryFunction, wasmEntryFunctions, etc.).
 *       Required.</li>
 * </ul>
 */
import React, { useEffect, useMemo, useState, useCallback, lazy, Suspense } from 'react';
import {
  Play, Save, AlertTriangle, CheckCircle, Loader2, Copy, FlaskConical,
  ShieldCheck, ShieldX, ShieldAlert, KeyRound, Clock as ClockIcon,
} from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Card } from '../Card';
import { Pill } from '../Pill';
import { invokeSkill } from '../../../services/api';

/* Lazy Monaco — same resilient pattern as RegoEditorSlideout. The
 * graceful fallback (`default: () => null`) makes the missing-module
 * case render as the textarea branch below, never throw. */
const MonacoEditor = lazy(() =>
  import('@monaco-editor/react')
    .then((mod) => ({ default: mod.default || mod.Editor || mod }))
    .catch(() => ({ default: () => null })),
);

/* Decision pill styling — aligned with PolicyDecisionsInventory so
 * the operator sees the same tone here and on the Step 7 Decisions
 * tab. */
const DECISION_PILL = {
  allowed:   { tone: 'success', label: 'allowed',   icon: ShieldCheck },
  submitted: { tone: 'cyan',    label: 'submitted', icon: ShieldCheck },
  blocked:   { tone: 'danger',  label: 'blocked',   icon: ShieldX },
  error:     { tone: 'danger',  label: 'error',     icon: ShieldAlert },
};

const FALLBACK_TEXTAREA_CLASS =
  'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface ' +
  'text-v2-ink text-[12px] font-mono-v2 whitespace-pre';

const INPUT_CLASS =
  'v2-focus-ring w-full px-2.5 py-1.5 rounded-v2-sm border border-v2-border ' +
  'bg-v2-surface text-v2-ink text-[13px]';

const DEFAULT_ARGS = `{
}`;

/* Derive the set of valid exports for a wasm skill — mirrors the
 * backend's authority order (wasm_entry_functions array → legacy
 * wasm_entry_function scalar → platform default "execute"). Used
 * both for the picker options and for the default selection. */
function deriveExports(skill) {
  const set = new Set();
  const arr = skill?.wasmEntryFunctions;
  if (Array.isArray(arr)) {
    for (const e of arr) {
      const t = String(e || '').trim();
      if (t) set.add(t);
    }
  }
  const legacy = skill?.wasmEntryFunction;
  if (legacy && String(legacy).trim()) set.add(String(legacy).trim());
  if (set.size === 0) set.add('execute');
  return Array.from(set);
}

export function SkillInvocationForm({ isOpen, onClose, agentId, skill }) {
  const isWasm = skill?.skillType === 'wasm_skill';
  const exports = useMemo(
    () => isWasm ? deriveExports(skill) : [],
    [isWasm, skill]);

  // Form state
  const [chosenExport, setChosenExport] = useState('');
  const [argsText, setArgsText] = useState(DEFAULT_ARGS);
  const [monacoFailed, setMonacoFailed] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState(null);   // success envelope
  const [blockedDetail, setBlockedDetail] = useState(null);  // {reason, kind, ...}
  const [errorDetail, setErrorDetail] = useState(null);      // {error, kind, ...}
  const [copied, setCopied] = useState(false);

  /* Re-seed state on each open or when the skill changes — a
   * stale draft from a previous invocation leaking into a fresh
   * one would be confusing in production ops. */
  useEffect(() => {
    if (!isOpen) return;
    setChosenExport(exports[0] || '');
    setArgsText(DEFAULT_ARGS);
    setSubmitting(false);
    setResponse(null);
    setBlockedDetail(null);
    setErrorDetail(null);
    setCopied(false);
  }, [isOpen, skill?.id, exports]);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    setResponse(null);
    setBlockedDetail(null);
    setErrorDetail(null);

    // Client-side JSON parse so a typo doesn't waste a backend
    // round-trip + produce a confusing policy-gate error message.
    let parsedArgs;
    if (!argsText || !argsText.trim()) {
      parsedArgs = {};
    } else {
      try { parsedArgs = JSON.parse(argsText); }
      catch (e) {
        setErrorDetail({
          error: `Args is not valid JSON: ${e.message}`,
          kind: 'CLIENT_JSON_PARSE',
        });
        setSubmitting(false);
        return;
      }
    }

    const r = await invokeSkill(agentId, skill?.id, {
      export: chosenExport || null,
      args:   parsedArgs,
    });

    // apiFetch envelope: success → unwrapped body; failure → {ok:false, error, status, ...body?}
    if (r?.ok === false) {
      // The backend returns 403 with a structured `blocked` body
      // for policy denials; apiFetch surfaces that as ok:false +
      // status:403 + the body merged into the envelope. We look for
      // the `blocked: true` marker the backend sets to distinguish
      // policy-gate denials from infrastructure errors.
      if (r.blocked === true || r.kind === 'POLICY_BLOCKED'
          || r.decision === 'blocked' || r.status === 403) {
        setBlockedDetail({
          reason: r.reason || r.error || 'Policy gate blocked execution',
          policyDecisionsRecorded: r.policyDecisionsRecorded ?? null,
        });
      } else {
        setErrorDetail({
          error: r.error || 'Invocation failed',
          kind:  r.kind || `HTTP_${r.status || 'UNKNOWN'}`,
          validExports: r.validExports,
        });
      }
      setSubmitting(false);
      return;
    }
    setResponse(r);
    setSubmitting(false);
  }, [agentId, skill?.id, chosenExport, argsText]);

  const onCopyOutput = useCallback(async () => {
    if (!response?.output) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(response.output, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  }, [response]);

  /* Step 6 / T4B — response classification. wasm_skill returns
   * { decision: 'allowed', output, ... }. Non-wasm dispatch returns
   * { decision: 'allowed', kind: 'WORKFLOW_TRIGGERED', message, ... }
   * which we render differently (no inline JSON output to show,
   * but the operator still gets the policy-gate verdict + the
   * follow-up advisory). */
  const isWorkflowTriggered = response?.kind === 'WORKFLOW_TRIGGERED';
  const verdict = response ? (isWorkflowTriggered ? 'submitted' : 'allowed')
                : blockedDetail ? 'blocked'
                : errorDetail   ? 'error'
                : null;
  const verdictPill = verdict ? DECISION_PILL[verdict] : null;
  const PillIcon = verdictPill?.icon;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={skill ? `Invoke ${skill.name}` : 'Invoke skill'}
      width={680}
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FlaskConical size={16} className="text-v2-action-strong" />
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            Run skill — structured
          </h2>
          {skill && (
            <Pill tone="muted" size="sm">{skill.skillType || '—'}</Pill>
          )}
          {skill?.riskLevel && (
            <Pill tone={skill.riskLevel === 'critical' ? 'danger'
                         : skill.riskLevel === 'high' ? 'warning' : 'muted'} size="sm">
              {skill.riskLevel}
            </Pill>
          )}
        </div>

        {skill && (
          <Card title="Target">
            <div className="p-3 grid grid-cols-2 gap-3 text-[12px]">
              <div>
                <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-0.5">Skill</span>
                <span className="text-v2-ink font-semibold">{skill.name}</span>
              </div>
              <div>
                <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-0.5">Skill ID</span>
                <code className="font-mono-v2 text-[11px] text-v2-ink-secondary break-all">{skill.id}</code>
              </div>
              {skill.description && (
                <div className="col-span-2">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-0.5">Description</span>
                  <p className="text-v2-ink text-[12.5px] leading-relaxed m-0">{skill.description}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Tier-4B — non-wasm skills now dispatch through the
            canonical ExecutionService.triggerWorkflow path. The form
            renders an explainer (the operator should know they're
            kicking off an async workflow, not running the code
            in-line like the wasm sandbox does) but still presents
            the JSON args editor + Invoke button. The response pane
            renders a WORKFLOW_TRIGGERED envelope rather than a JSON
            output. */}
        {!isWasm && (
          <div className="px-3 py-2 rounded-v2-sm bg-v2-action-soft/40 border border-v2-action/30 text-[12px] text-v2-ink leading-relaxed flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 shrink-0 text-v2-action-strong" />
            <span>
              Async dispatch — this <code className="font-mono-v2 text-[11px]">{skill?.skillType || '—'}</code>{' '}
              skill is invoked through the canonical workflow execution path
              (same dispatcher the conversational planner uses). The policy gate
              + audit chain entries are identical to the conversational path,
              so the Decisions tab will surface this invocation. Result will not
              return inline; check the workflow execution viewer for status.
            </span>
          </div>
        )}

        {/* Entry function picker — wasm only. For non-wasm types
            the workflow dispatcher routes by skill name, not by
            export, so the picker doesn't apply. */}
        {isWasm && (
          <Card title="Entry function">
            <div className="p-3 space-y-2">
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
                  <KeyRound size={10} style={{ display: 'inline', marginRight: 4 }} />
                  Export to call
                </span>
                <select
                  value={chosenExport}
                  onChange={(e) => setChosenExport(e.target.value)}
                  className={`${INPUT_CLASS} font-mono-v2`}
                >
                  {exports.map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
                <p className="text-[10.5px] text-v2-ink-muted mt-1">
                  First export is the skill's default. The chosen export
                  is recorded in the audit chain alongside the invocation.
                </p>
              </label>
            </div>
          </Card>
        )}

        {/* Tier-4B — Arguments editor now renders for ALL skill
            types. wasm forwards the bundle as policy input to the
            sandbox; non-wasm forwards it as args context the
            workflow execution path consumes. */}
        <Card title="Arguments (JSON)">
          <div className="p-3">
            <div style={{ height: 240 }} className="border border-v2-border rounded-v2-sm overflow-hidden">
              {monacoFailed ? (
                <textarea
                  value={argsText}
                  onChange={(e) => setArgsText(e.target.value)}
                  className={`${FALLBACK_TEXTAREA_CLASS} h-full block resize-none`}
                />
              ) : (
                <Suspense fallback={
                  <div className="h-full flex items-center justify-center text-[12px] text-v2-ink-muted">
                    <Loader2 size={14} className="animate-spin mr-2" /> Loading editor…
                  </div>
                }>
                  <MonacoEditor
                    height="100%"
                    language="json"
                    value={argsText}
                    onChange={(v) => setArgsText(v ?? '')}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 12,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      tabSize: 2,
                    }}
                    onMount={(_editor, monaco) => {
                      if (!monaco) setMonacoFailed(true);
                    }}
                  />
                </Suspense>
              )}
            </div>
            <p className="text-[10.5px] text-v2-ink-muted mt-2">
              {isWasm
                ? 'The JSON body is sent to the sandbox as the policy input and the export\'s argument bundle. Same payload shape the conversational path produces after planner inference.'
                : 'The JSON body is recorded in the audit chain as the structured args for this dispatch. The workflow execution path consumes it via the canonical skill-input contract.'}
            </p>
          </div>
        </Card>

        {/* Response pane — three states */}
        {verdict && (
          <Card title="Response">
            <div className="p-3 space-y-2 text-[12px]">
              <div className="flex items-center gap-2">
                <Pill tone={verdictPill.tone} size="sm" dot>
                  {PillIcon && <PillIcon size={11} style={{ display: 'inline', marginRight: 4 }} />}
                  {verdictPill.label}
                </Pill>
                {response?.elapsedMs != null && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] text-v2-ink-muted">
                    <ClockIcon size={10} /> {response.elapsedMs} ms
                  </span>
                )}
                {response?.export && (
                  <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">
                    export: {response.export}
                  </code>
                )}
                {response?.output && verdict === 'allowed' && (
                  <Button
                    variant="ghost" size="sm"
                    iconLeft={copied ? <CheckCircle size={11} /> : <Copy size={11} />}
                    onClick={onCopyOutput}
                    className="ml-auto"
                  >
                    {copied ? 'Copied' : 'Copy output'}
                  </Button>
                )}
              </div>

              {verdict === 'allowed' && (
                <pre className="px-3 py-2 rounded-v2-sm bg-v2-surface-muted text-[11px] font-mono-v2 text-v2-ink overflow-x-auto max-h-72">
                  {JSON.stringify(response.output ?? null, null, 2)}
                </pre>
              )}

              {verdict === 'submitted' && (
                <div className="px-3 py-2 rounded-v2-sm bg-v2-action-soft/40 border border-v2-action/30 text-[11.5px] text-v2-ink leading-relaxed">
                  <div className="font-semibold mb-1 text-v2-action-strong">Workflow triggered</div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    {response.skillType && (
                      <div>
                        <span className="text-v2-ink-muted">Skill type: </span>
                        <code className="font-mono-v2">{response.skillType}</code>
                      </div>
                    )}
                    {response.skillName && (
                      <div>
                        <span className="text-v2-ink-muted">Skill name: </span>
                        <span className="font-semibold">{response.skillName}</span>
                      </div>
                    )}
                    {response.mode && (
                      <div>
                        <span className="text-v2-ink-muted">Mode: </span>
                        <code className="font-mono-v2">{response.mode}</code>
                      </div>
                    )}
                    {response.policyDecisionsRecorded != null && (
                      <div>
                        <span className="text-v2-ink-muted">Policy decisions: </span>
                        <span className="font-mono-v2">{response.policyDecisionsRecorded}</span>
                      </div>
                    )}
                  </div>
                  {response.message && (
                    <p className="mt-1.5 text-[11px] text-v2-ink-muted italic m-0">
                      {response.message}
                    </p>
                  )}
                </div>
              )}

              {verdict === 'blocked' && (
                <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[11.5px] text-v2-danger-strong">
                  <div className="font-semibold mb-1">Policy gate blocked execution</div>
                  <div>{blockedDetail.reason}</div>
                  {blockedDetail.policyDecisionsRecorded != null && (
                    <div className="mt-1 text-v2-ink-muted text-[10.5px]">
                      {blockedDetail.policyDecisionsRecorded} policy decision
                      {blockedDetail.policyDecisionsRecorded === 1 ? '' : 's'}{' '}
                      recorded — review under the <em>Decisions</em> tab.
                    </div>
                  )}
                </div>
              )}

              {verdict === 'error' && (
                <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[11.5px] text-v2-danger-strong">
                  <div className="font-semibold mb-1">
                    {errorDetail.kind || 'Error'}
                  </div>
                  <div>{errorDetail.error}</div>
                  {Array.isArray(errorDetail.validExports) && errorDetail.validExports.length > 0 && (
                    <div className="mt-1 text-v2-ink-muted text-[10.5px]">
                      Valid exports: {errorDetail.validExports.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button
            variant="primary" size="sm"
            disabled={submitting || !skill?.id}
            onClick={onSubmit}
            iconLeft={submitting
              ? <Loader2 size={13} className="animate-spin" />
              : <Play size={13} />}
            title={isWasm
              ? 'Run the chosen export against the supplied args (synchronous, returns sandbox output)'
              : 'Dispatch this skill through the workflow execution path (async; check workflow viewer for result)'}
          >
            {submitting ? 'Invoking…' : (isWasm ? 'Invoke' : 'Dispatch')}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

export default SkillInvocationForm;
